/**
 * Tests for ViewGridToolbar component.
 *
 * Verifies button visibility, disabled states, and callback invocations.
 */

import React from "react";
import { render, screen, fireEvent } from "@testing-library/react";
import "@testing-library/jest-dom";
import ViewGridToolbar from "./ViewGridToolbar";

// Mock the ExportButton since it has its own hook dependencies
jest.mock("./ExportButton", () => {
  return function MockExportButton() {
    return <button data-testid="export-button">Export</button>;
  };
});

describe("ViewGridToolbar", () => {
  const defaultProps = {
    viewId: "view-123",
    onAddRecord: jest.fn(),
    canAddRecord: true,
    isAddingRecord: false,
    isLoading: false,
  };

  beforeEach(() => {
    jest.clearAllMocks();
  });

  describe("Add Record Button", () => {
    it("renders Add Record button", () => {
      render(<ViewGridToolbar {...defaultProps} />);
      expect(
        screen.getByRole("button", { name: /add record/i }),
      ).toBeInTheDocument();
    });

    it("calls onAddRecord when clicked", () => {
      render(<ViewGridToolbar {...defaultProps} />);
      fireEvent.click(screen.getByRole("button", { name: /add record/i }));
      expect(defaultProps.onAddRecord).toHaveBeenCalledTimes(1);
    });

    it("disables button when canAddRecord is false", () => {
      render(<ViewGridToolbar {...defaultProps} canAddRecord={false} />);
      expect(
        screen.getByRole("button", { name: /add record/i }),
      ).toBeDisabled();
    });

    it("disables button when isAddingRecord is true", () => {
      render(<ViewGridToolbar {...defaultProps} isAddingRecord={true} />);
      expect(
        screen.getByRole("button", { name: /add record/i }),
      ).toBeDisabled();
    });

    it("disables button when isLoading is true", () => {
      render(<ViewGridToolbar {...defaultProps} isLoading={true} />);
      expect(
        screen.getByRole("button", { name: /add record/i }),
      ).toBeDisabled();
    });

    it("disables button when no onAddRecord callback", () => {
      render(<ViewGridToolbar {...defaultProps} onAddRecord={undefined} />);
      expect(
        screen.getByRole("button", { name: /add record/i }),
      ).toBeDisabled();
    });
  });

  describe("Bulk Delete Button", () => {
    it("not visible when no rows selected", () => {
      render(
        <ViewGridToolbar
          {...defaultProps}
          selectedCount={0}
          onBulkDelete={jest.fn()}
        />,
      );
      expect(
        screen.queryByRole("button", { name: /delete selected/i }),
      ).not.toBeInTheDocument();
    });

    it("visible with count when rows are selected", () => {
      render(
        <ViewGridToolbar
          {...defaultProps}
          selectedCount={3}
          onBulkDelete={jest.fn()}
        />,
      );
      expect(
        screen.getByRole("button", { name: /delete selected \(3\)/i }),
      ).toBeInTheDocument();
    });

    it("calls onBulkDelete when clicked", () => {
      const onBulkDelete = jest.fn();
      render(
        <ViewGridToolbar
          {...defaultProps}
          selectedCount={2}
          onBulkDelete={onBulkDelete}
        />,
      );
      fireEvent.click(screen.getByRole("button", { name: /delete selected/i }));
      expect(onBulkDelete).toHaveBeenCalledTimes(1);
    });

    it("not visible when onBulkDelete callback missing", () => {
      render(<ViewGridToolbar {...defaultProps} selectedCount={5} />);
      expect(
        screen.queryByRole("button", { name: /delete selected/i }),
      ).not.toBeInTheDocument();
    });
  });

  describe("Import CSV Button", () => {
    it("not visible when onImportCsv not provided", () => {
      render(<ViewGridToolbar {...defaultProps} />);
      expect(
        screen.queryByRole("button", { name: /import csv/i }),
      ).not.toBeInTheDocument();
    });

    it("visible and clickable when onImportCsv provided", () => {
      const onImportCsv = jest.fn();
      render(<ViewGridToolbar {...defaultProps} onImportCsv={onImportCsv} />);
      const btn = screen.getByRole("button", { name: /import csv/i });
      expect(btn).toBeInTheDocument();
      fireEvent.click(btn);
      expect(onImportCsv).toHaveBeenCalledTimes(1);
    });
  });

  describe("Audit Log Button", () => {
    it("not visible when onAuditLog not provided", () => {
      render(<ViewGridToolbar {...defaultProps} />);
      expect(
        screen.queryByRole("button", { name: /audit log/i }),
      ).not.toBeInTheDocument();
    });

    it("visible and clickable when onAuditLog provided", () => {
      const onAuditLog = jest.fn();
      render(<ViewGridToolbar {...defaultProps} onAuditLog={onAuditLog} />);
      const btn = screen.getByRole("button", { name: /audit log/i });
      expect(btn).toBeInTheDocument();
      fireEvent.click(btn);
      expect(onAuditLog).toHaveBeenCalledTimes(1);
    });
  });

  describe("Export Button", () => {
    it("renders export button", () => {
      render(<ViewGridToolbar {...defaultProps} />);
      expect(screen.getByTestId("export-button")).toBeInTheDocument();
    });
  });
});
